<template>
  <div class="q-pa-md q-gutter-y-sm q-mx-auto" style="max-width: 600px">

    <div v-for="(distanceFee,typekey) in distanceFeesData" :key="typekey">
      <q-card flat bordered>
        <q-card-section class="q-pa-sm row justify-between items-center">
          <div class="text-subtitle1 text-bold distance-fee-type-labels">
            <q-icon
              name="person"
              style="font-size: 25px; padding-right: 0.1rem"
            />
            {{typekey}} Fees
          </div>
          <q-btn
            v-if="canUpdateFees"
            color="primary"
            label="Save"
            size="sm"
            :loading="distanceFee.saving"
            @click="handleSaveDistanceFee(typekey)"
          />
        </q-card-section>

        <q-separator />
        <q-card-section v-if="!distanceFee.loading">
          <div
            class="row items-center q-col-gutter-x-sm"
            :class="{'bg-grey-2':index%2==0}"
            v-for="(rule, index) in distanceFee.items"
            :key="index"
          >
            <app-editable-input
              class="col"
              :readonly="!canUpdateFees"
              :data="rule.minMileage"
              type="number"
              min="0"
              text="Min Miles *"
              name="minMileage"
              @input="handleDistanceFeeChange(typekey, index, $event)"
            />
            <app-editable-input
              class="col"
              :readonly="!canUpdateFees"
              :data="rule.maxMileage"
              type="number"
              min="0"
              text="Max Miles *"
              name="maxMileage"
               @input="handleDistanceFeeChange(typekey, index, $event)"
            />
            <app-editable-input
              class="col"
              :readonly="!canUpdateFees"
              :data="rule.fee"
              type="number"
              text="Fee *"
              name="fee"
              prefix="$"
               @input="handleDistanceFeeChange(typekey, index, $event)"
            />
            <div v-if="canUpdateFees">
              <q-btn
                dense
                size="sm"
                outline
                color="primary"
                icon="add"
                @click="handleAddDistanceFee(typekey,index)"
              />
            </div>
            <div
              v-if="canUpdateFees"
              :class="`${
                index !== 0 || (`${typekey}Fees` && `${typekey}Fees`.length > 0)
                  ? 'visible'
                  : 'invisible'
              }`"
            >
              <q-btn
                dense
                size="sm"
                outline
                color="negative"
                icon="delete"
                @click="handleDeleteDistanceFee(typekey,index)"
              />
            </div>
          </div>
        </q-card-section>
        <q-card-section v-else>
          <div class="q-py-xs">
            <q-skeleton type="rect" height="32px" animation="fade" />
          </div>
          <div class="q-py-xs">
            <q-skeleton type="rect" height="32px" animation="fade" />
          </div>
          <div class="q-py-xs">
            <q-skeleton type="rect" height="32px" animation="fade" />
          </div>
        </q-card-section>
      </q-card>
    </div>
  </div>
</template>

<script>
import graphql from '@client/graphql';
import { assessPermission } from '@server/lib/permission-helpers';
import { sanitizeObject } from '@client/utils/object-helpers';
import AppEditableInput from '@client/components/form/editable-input';
import { DISTANCE_FEE_TYPES } from '@server/constants/fees';

const distanceFeesData = {
  enclosed: {
    type: DISTANCE_FEE_TYPES.enclosed,
    loading: false,
    saving: false,
    items: [],
    deleted: []
  },
  inop: {
    type: DISTANCE_FEE_TYPES.inop,
    loading: false,
    saving: false,
    items: [],
    deleted: []
  }
};

export default {
  name: 'AppGeneralModifiers',
  components: {
    AppEditableInput
  },
  apollo: {
    currentUser: {
      query: graphql.queries.currentUser
    }
  },
  data() {
    return {
      error: null,
      loadingFees: false,
      savingFees: false,
      distanceFeesData
    };
  },
  computed: {
    canUpdateFees() {
      return assessPermission.taq.canUpdate(this.currentUser);
    }
  },
  created() {
    Object.keys(distanceFeesData).forEach((kt) => {
      const df = distanceFeesData[kt];
      this.$apollo.addSmartQuery(`${kt}Fees`, {
          query: graphql.queries.getDistanceFees,
          variables() {
            return { type: df.type };
          },
          watchLoading(isLoading) {
            this.distanceFeesData[kt].loading = isLoading;
          },
          error(error) {
            this.error = error;
          },
          result(result) {
            if (result.loading) return;
            if (result.data.distanceFees.length === 0) {
              this.distanceFeesData[kt].items = [
                {
                  minMileage: 0,
                  maxMileage: 0,
                  fee: 0,
                  order: 0,
                  type: df.type
                }
              ];
            } else {
              this.distanceFeesData[kt].items = result.data.distanceFees.sort(
                (a, b) => a.order - b.order
              );
            }
          },
          update(data) {
            return data.distanceFees;
          }
      });
    });
  },
  methods: {
    handleDistanceFeeChange(type, index, event) {
      this.distanceFeesData[type].items = this.distanceFeesData[type].items.map((d, i) => {
        if (index === i) {
          return {
            ...d,
            [event.name]: parseInt(event.model, 10)
          };
        }
        return d;
      });
    },
    handleAddDistanceFee(type, index) {
      const data = [...this.distanceFeesData[type].items];
      data.splice(index + 1, 0, {
        minMileage: 0,
        maxMileage: 0,
        fee: 0,
        type: DISTANCE_FEE_TYPES[type]
      });
       this.distanceFeesData[type].items = data.map((d, i) => ({ ...d, order: i }));
    },
    handleDeleteDistanceFee(type, index) {
      if (this.distanceFeesData[type].items[index].id) {
        this.distanceFeesData[type].deleted.push(this.distanceFeesData[type].items[index].id);
      }

      const data = [...this.distanceFeesData[type].items];
      data.splice(index, 1);
       this.distanceFeesData[type].items = data.map((d, i) => ({ ...d, order: i }));
    },

    async handleSaveDistanceFee(type) {
      this.distanceFeesData[type].saving = true;

      try {
        if (this.distanceFeesData[type].deleted.length > 0) {
          await this.$apollo.mutate({
            mutation: graphql.mutations.deleteDistanceFees,
            variables: {
              ids: this.distanceFeesData[type].deleted
            }
          });
        }

        const response = await this.$apollo.mutate({
          mutation: graphql.mutations.updateDistanceFees,
          variables: {
            feeData: this.distanceFeesData[type].items.map((d) => sanitizeObject(d))
          }
        });

        this.distanceFeesData[type].items = response.data.updateDistanceFees.sort(
                (a, b) => a.order - b.order
        );
        this.distanceFeesData[type].deleted = [];
        this.notifyPositive(`${type.charAt(0).toUpperCase() + type.slice(1)} Fees have been saved successfully!`);
      } catch (error) {
        this.notifyNegative(error.message);
      }

      this.distanceFeesData[type].saving = false;
    }
  }
};
</script>

<style scoped>
  .distance-fee-type-labels{
    text-transform:capitalize
  }
</style>
